/** Parsed server error body: Messages mapped by field name and/or global */
export type ServerErrorBody = {
  [key: string]: string | string[];
};

/** Server Error with parsed body */
export class ServerError extends Error {
  status: number;
  body: ServerErrorBody;
  constructor(status: number, body: ServerErrorBody) {
    const message = Object.values(body || {})
      .flat()
      .join("; ");
    super(message || `Error ${status}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * Backend has 4 Error Response Formats
 * 1. Generic Error (most common)
 *  Used for: auth errors, not found, business logic errors.
 *
 * {
 *  "error_code": "ID120",
 *  "message": "Authentication failed"
 * }
 *
 * 2. Validation Error (returned as an array)
 *  Used for: request body validation, JSON parsing errors.
 *
 * [
 *  {
 *     "error_code": "ID111",
 *    "message": "field must be at least 3 characters long",
 *     "field": "name"
 *   }
 * ]
 *
 * 3. Database Error
 *  Used for: constraint violations (23XXX codes), data exceptions (22XXX codes).
 *
 * {
 *   "error_code": "23505",
 *   "message": "...",
 *   "columns": ["col1", "col2"],
 *   "hint": "",
 *   "constraint": "constraint_name",
 *   "detail": "Key (...)=(...) already exists."
 * }
 *
 * 4. Message-only Error
 *  Used for: some gateway/license errors.
 *
 * {
 *   "message": "Licence expired"
 * }
 */

interface GeneralServerError {
  error_code: string;
  message: string;
}

interface MessageOnlyServerError {
  message: string;
}

interface ServerValidationErrorItem extends GeneralServerError {
  field: string | null;
}

type ServerValidationError = ServerValidationErrorItem[];

interface ServerDatabaseError extends GeneralServerError {
  columns: string[];
  hint: string;
  constraint: string;
  detail: string;
}

const isServerError = (value: unknown): value is GeneralServerError =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "error_code" in value &&
      "message" in value,
  );

const isMessageOnlyServerError = (
  value: unknown,
): value is MessageOnlyServerError =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "message" in value &&
      typeof value.message === "string",
  );

const isServerValidationErrorItem = (
  value: unknown,
): value is ServerValidationErrorItem =>
  Boolean(value && isServerError(value) && "field" in value);

const isServerValidationError = (
  value: unknown,
): value is ServerValidationError =>
  Boolean(
    value &&
      Array.isArray(value) &&
      value.every((item) => isServerValidationErrorItem(item)),
  );

const isServerDatabaseError = (value: unknown): value is ServerDatabaseError =>
  isServerError(value) && "columns" in value && "hint" in value;

const normalizeValidationFieldName = (field?: string | null) => {
  if (typeof field !== "string") {
    return "global";
  }
  // Remove "default_" from the beginning of field names (for backend related reasons)
  const trimmed = field.replace(/^default_/, "").trim();
  return trimmed.length > 0 ? trimmed : "global";
};

const appendMessage = (
  current: string | string[] | undefined,
  message: string,
): string | string[] => {
  if (!current) {
    return message;
  }
  if (Array.isArray(current)) {
    return [...current, message];
  }
  return [current, message];
};

const joinNonEmptyParts = (...parts: (string | undefined)[]): string =>
  parts
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0)
    .join(". ");

const mapValidationErrorsToServerErrorBody = (
  errors: ServerValidationError,
): ServerErrorBody => {
  return errors.reduce((acc, error) => {
    const message = error.message || error.error_code || "Validation error";
    const field = normalizeValidationFieldName(error.field ?? undefined);
    acc[field] = appendMessage(acc[field], message);
    return acc;
  }, {} as ServerErrorBody);
};

const mapDatabaseErrorToServerErrorBody = (
  error: ServerDatabaseError,
): ServerErrorBody => {
  const baseMessage = error.detail || error.message || error.error_code;
  const message = joinNonEmptyParts(baseMessage, error.hint);

  if (!error.columns || error.columns.length === 0) {
    return { global: message };
  }

  const fieldMappedErrors = error.columns.reduce((acc, column) => {
    const normalizedField = normalizeValidationFieldName(column);
    if (normalizedField) {
      acc[normalizedField] = appendMessage(acc[normalizedField], message);
    }
    return acc;
  }, {} as ServerErrorBody);
  return fieldMappedErrors;
};

export const parseErrorBody = (
  res: Response,
  body: unknown,
): ServerErrorBody => {
  if (isServerValidationError(body)) {
    return mapValidationErrorsToServerErrorBody(body);
  }

  if (isServerDatabaseError(body)) {
    return mapDatabaseErrorToServerErrorBody(body);
  }

  if (isServerError(body)) {
    const message = body.message || body.error_code;
    return { global: message };
  }

  if (isMessageOnlyServerError(body)) {
    return { global: body.message };
  }

  return { global: `${res.status} Server Error` };
};
